import React, {useState} from 'react';
import {FormFieldOption, FormFieldSelectOptions, SelectFieldStylesBag} from '@src/@types/components/Form';
import ReactSelect, {
    components,
    CSSObjectWithLabel,
    DropdownIndicatorProps,
    mergeStyles,
    MultiValue,
    SingleValue
} from 'react-select';
import {useSelector} from 'react-redux';
import trans from '@src/helpers/trans';
import classnames from '@src/helpers/classnames';
import {IReduxStoreState} from '@src/@interfaces';
import app from '@src/helpers/app';
import MutableHex from '@src/helpers/colors/MutableHex';
import {CustomSelectOnChangePayloadItem, CustomSelectProps} from '@src/@types/components/Fields/CustomSelect';
import {randomString} from '@src/helpers/rand';
import {isMobileViewport} from '@src/helpers/isMobileViewport';
import {isNumber} from '@src/services/DotAccessor/types-utils';

const blankPixel = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

const availableMarkerKeys = [
    {
        sideSize: 10,
        match: (data: any) => ('marker' in data) && Boolean(data.marker),
        makeData: (backgroundColor: string) => ({marker: backgroundColor}),
        makeEmptyData: () => ({marker: 'transparent'}),
        getMarkerBackground: (data: any) => data.marker,
    },
    {
        sideSize: 20,
        match: (data: any) => ('avatar_public_path' in data) && Boolean(data.avatar_public_path),
        makeData: (backgroundImage: string) => ({avatar_public_path: backgroundImage}),
        makeEmptyData: () => ({avatar_public_path: blankPixel}),
        getMarkerBackground: (data: any) => `url(${data.avatar_public_path})`,
    },
    {
        sideSize: 20,
        match: (data: any) => ('icon_public_path' in data) && Boolean(data.icon_public_path),
        makeData: (backgroundImage: string) => ({icon_public_path: backgroundImage}),
        makeEmptyData: () => ({icon_public_path: blankPixel}),
        getMarkerBackground: (data: any) => `url(${data.icon_public_path})`,
    }
];

export const defaultComponents = components;

const getMarker = (data: any) => {
    if (!data || typeof data !== 'object') {
        return null;
    }

    return availableMarkerKeys.find(item => item.match(data)) ?? null;
};

const makeComputedStylesAccessor = () => {
    const styles: CSSStyleDeclaration = getComputedStyle(document.body);

    return (property: string) => styles.getPropertyValue(property).trim();
};

export function getMarkerStyles(data: unknown, marker: ReturnType<typeof getMarker> | null = null): CSSObjectWithLabel {
    marker ??= getMarker(data);
    if (!marker) {
        return {};
    }

    return {
        alignItems: 'center',
        display: 'flex',

        ':before': {
            background: marker.getMarkerBackground(data),
            backgroundSize: 'cover',
            borderRadius: '50%',
            content: '" "',
            display: 'block',
            marginRight: 8,
            height: marker.sideSize,
            width: marker.sideSize,
        },
    };
}

export function getSelectStyles(optionsMarker: ReturnType<typeof getMarker> | null = null): SelectFieldStylesBag {
    const get = makeComputedStylesAccessor();
    const isLightTheme = app('theme').activeThemeIsLight();

    const makeMarker = (data: unknown = null, marker: typeof optionsMarker = null) => {
        if (!optionsMarker) {
            return {};
        }

        return getMarkerStyles(
                data != null ? data : (marker ?? optionsMarker).makeEmptyData(),
                marker ?? optionsMarker
        );
    };
    const shadeCssVar = (variableName: string, percent: number) => {
        return new MutableHex(get(variableName), '').linearShade(percent).hex();
    };

    return {
        default: {
            control: (defaultStyles, {isFocused, isDisabled}) => ({
                ...defaultStyles,
                minHeight: '36.53px',
                boxShadow: undefined,
                backgroundColor: get(isDisabled ? '--bs-input-disabled-bg' : '--bs-input-bg'),
                borderColor: get(
                        isDisabled ? '--bs-input-disabled-border-color' : (
                                isFocused ? '--bs-input-focus-border-color' : '--bs-input-border-color'
                        )
                ),

                '&:hover': {
                    borderColor: get(
                            isDisabled ? '--bs-input-disabled-border-color' : (
                                    isFocused ? '--bs-input-focus-border-color' : '--bs-input-border-color'
                            )
                    ),
                },
            }),
            input: defaultStyles => ({
                ...defaultStyles,
                ...makeMarker(),
                color: get('--bs-input-color'),
            }),
            menu: defaultStyles => ({
                ...defaultStyles,
                minWidth: '200px',
                zIndex: 4,
                backgroundColor: get('--bs-input-bg'),
                boxShadow: undefined,
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: get('--bs-input-border-color'),
            }),
            menuPortal: defaultStyles => ({
                ...defaultStyles,
                zIndex: 10000,
            }),
            multiValue: (defaultValue) => ({
                ...defaultValue,
                backgroundColor: shadeCssVar('--bs-gray-300', isLightTheme ? -15 : 15),
            }),
            multiValueLabel: (defaultValue, {data}) => ({
                ...defaultValue,
                ...makeMarker(data),
                color: get('--bs-input-color'),
            }),
            multiValueRemove: defaultValue => ({
                ...defaultValue,
                ':hover': {
                    backgroundColor: shadeCssVar('--bs-danger', 75),
                    color: get('--bs-danger'),
                }
            }),
            option: (defaultStyles, {isFocused, isDisabled, data}) => {
                return ({
                    ...defaultStyles,
                    ...makeMarker(data),
                    backgroundColor: (isFocused && !isDisabled) ? get('--bs-gray-400') : 'transparent',
                    cursor: 'pointer',
                    color: (isFocused && !isDisabled) ?
                            get(isLightTheme ? '--bs-gray-700' : '--bs-gray-200') :
                            get('--bs-input-color'),

                    ':active': {
                        backgroundColor: (isFocused && !isDisabled) ? get('--bs-gray-400') : 'transparent',
                    }
                });
            },
            placeholder: defaultStyles => {
                const color = isLightTheme ?
                        get('--bs-gray-600') :
                        shadeCssVar('--bs-gray-400', -30);

                const marker = getMarker({marker: 'transparent'});

                return ({
                    ...defaultStyles,
                    ...makeMarker({marker: color}, marker),
                    color: color,
                });
            },
            singleValue: (defaultStyles, {isDisabled, data}) => ({
                ...defaultStyles,
                ...makeMarker(data),
                color: isDisabled ?
                        shadeCssVar('--bs-input-color', 20) :
                        get('--bs-input-color'),
            }),
            clearIndicator: (defaultStyles) => ({
                ...defaultStyles,
                padding: '6px 2px 7px',
                color: shadeCssVar('--bs-input-color', 30 * (isLightTheme ? 1 : -1)),
                ':hover': {
                    color: get('--bs-input-color'),
                }
            }),
            dropdownIndicator: (defaultStyles) => ({
                ...defaultStyles,
                padding: '6px 2px 7px',
                color: shadeCssVar('--bs-input-color', 30 * (isLightTheme ? 1 : -1)),
                ':hover': {
                    color: get('--bs-input-color'),
                }
            }),
            loadingIndicator: (defaultStyles) => ({
                ...defaultStyles,
                padding: '6px 2px 7px',
                color: shadeCssVar('--bs-input-color', 30 * (isLightTheme ? 1 : -1)),
                ':hover': {
                    color: get('--bs-input-color'),
                }
            }),
        },

        invalid: {
            control: (defaultStyles) => ({
                ...defaultStyles,
                borderColor: get('--bs-danger'),

                '&:hover': {
                    borderColor: get('--bs-danger'),
                },
            }),
        },

        dark: {},

        light: {},

        sm: {
            clearIndicator: defaultStyles => ({...defaultStyles, padding: '2px 0 3px'}),
            dropdownIndicator: defaultStyles => ({...defaultStyles, padding: '2px 0 3px'}),
            loadingIndicator: defaultStyles => ({...defaultStyles, padding: '2px 0 3px'}),
            control: defaultStyles => ({...defaultStyles, minHeight: '28px'}),
            menuList: defaultStyles => ({...defaultStyles, fontSize: '0.8em'}),
            placeholder: defaultStyles => ({...defaultStyles, fontSize: '0.8em'}),
            singleValue: defaultStyles => ({...defaultStyles, fontSize: '0.8em'}),
            multiValue: defaultStyles => ({...defaultStyles, fontSize: '0.8em'}),
            multiValueLabel: defaultStyles => ({...defaultStyles, padding: '2px 1px 1px 4px'}),
            indicatorSeparator: defaultStyles => ({...defaultStyles, marginTop: '4px', marginBottom: '4px'}),
            input: defaultStyles => ({...defaultStyles, margin: 0, paddingTop: 0, paddingBottom: 0}),
        },

        lg: {},
    };
}

function DropdownIndicatorSm(props: DropdownIndicatorProps) {
    return <components.DropdownIndicator {...props}>
        <components.DownChevron size={10}/>
    </components.DropdownIndicator>;
}

export const prepareRawOptions = <Option extends FormFieldOption, >(
        options: FormFieldSelectOptions | Option[],
        lang?: string
): Option[] => {
    if (typeof options === 'string') {
        return [];
    }

    if (!Array.isArray(options) && Object.keys(options).every(key => isNumber(key))) {
        options = Object.values(options);
    }

    if (Array.isArray(options)) {
        return options.map<Option>(option => {
            if (option != null && typeof option === 'object') {
                return option;
            }

            return {
                value: option,
                label: String(option)
            } as Option;
        });
    }

    const result: Option[] = [];
    for (const [value, label] of Object.entries(options)) {
        result.push({value, label: trans(label, lang)} as Option);
    }

    return result;
};

const valueResolverValidator = (value: any) => value == null ? null : value;

export default function CustomSelect<
        IsMultiple extends boolean | unknown = false,
        Option extends FormFieldOption = FormFieldOption<CustomSelectOnChangePayloadItem>
>(props: CustomSelectProps<IsMultiple, Option>) {
    const isLightTheme = useSelector((state: IReduxStoreState) => state.theme.light);
    const [fieldName] = useState(props.name ?? randomString(12));
    const [formName] = useState(props.formName ?? randomString(12));

    const id = props.inputId ?? `${formName ?? Date.now()}@${fieldName}`;
    const label = trans(props.label);
    const isMultiple = props.multiple ?? false;
    const valueResolver = props.valueResolver ?? ((value: any, options) => {
        if (isMultiple) {
            return options.filter(option => value?.includes(option.value));
        }

        return options.find(option => option.value === value);
    });

    const defaultOptions: Option[] = [];
    if (props.emptyOption) {
        defaultOptions.push({
            value: null,
            label: trans(props.emptyOption),
        } as unknown as Option);
    }

    const fieldOptions = prepareRawOptions<Option>(props.options ?? []);

    const options = [...defaultOptions, ...fieldOptions];
    const optionWithMarker = options.find(o => getMarker(o) != null);

    const selectStyles = getSelectStyles(getMarker(optionWithMarker));
    let customStyle = mergeStyles(selectStyles.default, isLightTheme ? selectStyles.light : selectStyles.dark);
    const customComponents = props.customComponents ?? {};

    if (props.size === 'sm') {
        if (!customComponents.DropdownIndicator) {
            customComponents.DropdownIndicator = DropdownIndicatorSm;
        }
        customStyle = mergeStyles(customStyle, selectStyles.sm);
    } else if (props.size === 'lg') {
        customStyle = mergeStyles(customStyle, selectStyles.lg);
    }

    if (props.isInvalid) {
        customStyle = mergeStyles(customStyle, selectStyles.invalid);
    }

    if (props.customStyles) {
        customStyle = mergeStyles(customStyle, props.customStyles);
    }

    const isDisabled = props.disabled || props.readOnly;

    return <ReactSelect
            {...(props.attributes ?? {})}
            name={fieldName}
            inputId={id}
            className={classnames('custom-select', props.className, {
                'is-invalid': props.isInvalid,
            })}
            aria-invalid={props.isInvalid}
            placeholder={trans(props.placeholder ?? 'Forms.choose_field_option', {
                field: label.toLowerCase()
            })}
            options={options}
            value={valueResolverValidator(valueResolver(props.value, options))}
            closeMenuOnSelect={props.closeMenuOnSelect ?? !isMultiple}
            isMulti={isMultiple}
            isClearable={props.clearable === true}
            isDisabled={isDisabled}
            isSearchable={
                props.searchable != null ?
                        props.searchable :
                        (isMobileViewport() ? options.length > 5 : !isDisabled)
            }
            isLoading={props.isLoading}
            menuIsOpen={props.readOnly === true ? false : undefined}
            onBlur={props.onBlur}
            menuPortalTarget={props.menuPortalTarget}
            menuPosition={props.menuPortalTarget != null ? 'absolute' : undefined}
            onChange={value => {
                let payload, item;
                if (isMultiple) {
                    item = value as MultiValue<typeof options[number]>;
                    payload = item.map(i => i.value ?? null);
                } else {
                    item = value as SingleValue<typeof options[number]>;
                    payload = (item?.value ?? null);
                }

                props.onChange(
                        payload as Parameters<typeof props['onChange']>[0],
                        item as Parameters<typeof props['onChange']>[1],
                );
            }}
            theme={theme => {
                if (props.customTheme) {
                    theme = props.customTheme(theme);
                }

                return theme;
            }}
            styles={customStyle}
            components={customComponents}
            form={props.formId}
            filterOption={props.filterOption}
    />;
}
